import { useCallback, useState } from 'react';
import { AuthPinUseCase } from '@/application/use-cases/AuthPinUseCase';
import { HttpAuthPinRepository } from '@/infrastructure/repositories/HttpAuthPinRepository';
import { IndexedDbActiveSessionRepository } from '@/infrastructure/repositories/IndexedDbActiveSessionRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';

const authPinUseCase = new AuthPinUseCase(
  new HttpAuthPinRepository(),
  new IndexedDbActiveSessionRepository()
);

type AuthPinStatus = 'idle' | 'loading' | 'error';

interface UsePinAuthenticationResult {
  status: AuthPinStatus;
  errorMessage: string | null;
  authenticate: (pin: string, deviceLabel: string) => Promise<DeviceSession | null>;
}

/**
 * Encapsula a autenticação de dispositivo via PIN (HUB-147). Mantém a sub-
 * sessão de dispositivo (`device_session`) fora deste hook em memória —
 * quem chama decide o que fazer com o resultado; desde a HUB-236, o próprio
 * `AuthPinUseCase` também espelha a sessão em IndexedDB para uso do Service
 * Worker (ADR-003, Adendo — Correção 2), de forma transparente a este hook.
 */
export function usePinAuthentication(): UsePinAuthenticationResult {
  const [status, setStatus] = useState<AuthPinStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const authenticate = useCallback(
    async (pin: string, deviceLabel: string): Promise<DeviceSession | null> => {
      setStatus('loading');
      setErrorMessage(null);

      const result = await authPinUseCase.execute({ pin, deviceLabel });

      if (!result.success) {
        setStatus('error');
        setErrorMessage(result.error);
        return null;
      }

      setStatus('idle');
      return result.session;
    },
    []
  );

  return { status, errorMessage, authenticate };
}
